import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade5EngineeringLesson.css";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

/** Helper to shuffle arrays */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * 3D Demo: cycles among "Robot Arm," "Drone," and "Smart House"
 * to illustrate relevant examples for Grade 5.
 */
function Grade5Engineering3DDemo() {
  const items = [
    {
      name: "Robot Arm",
      description: "Automated arms help build products in factories!",
      fact: "Engineers program precise movements and use sensors.",
    },
    {
      name: "Drone",
      description: "Drones can deliver packages or survey land from above!",
      fact: "Engineers optimize weight, motors, and flight control systems.",
    },
    {
      name: "Smart House",
      description: "Houses with automated lights and sensors save energy!",
      fact: "Engineers integrate IoT devices for comfort and efficiency.",
    },
  ];

  const [index, setIndex] = useState(0);
  const nextItem = () => setIndex((prev) => (prev + 1) % items.length);

  return (
    <div className="g5eng-3d-demo-container">
      <h4>3D Engineering Demo: {items[index].name}</h4>
      <p className="g5eng-demo-description">{items[index].description}</p>
      <p className="g5eng-demo-fact">
        <strong>Fun Fact:</strong> {items[index].fact}
      </p>
      <button className="g5eng-3d-next-btn" onClick={nextItem}>
        See Another Example
      </button>

      <div style={{ width: "300px", height: "200px", margin: "10px auto", background: "#f0f0f0" }}>
        <Canvas camera={{ position: [0, 2, 5], fov: 50 }}>
          <OrbitControls enablePan={false} />
          <ambientLight intensity={0.5} />
          <directionalLight position={[5, 5, 5]} />

          {/* Ground plane */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1, 0]}>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color="#ddd" />
          </mesh>

          {/* Switch shape by name */}
          <Grade5EngineeringShape name={items[index].name} />
        </Canvas>
      </div>
    </div>
  );
}

/** 
 * Grade5EngineeringShape:
 *  - "Robot Arm": base + rotating segments
 *  - "Drone": basic body + 4 small propellers
 *  - "Smart House": box with a "roof" + small solar panel
 */
function Grade5EngineeringShape({ name }) {
  if (name === "Robot Arm") {
    return (
      <group>
        {/* Base */}
        <mesh>
          <cylinderGeometry args={[0.3, 0.3, 0.1, 16]} />
          <meshStandardMaterial color="#616161" />
        </mesh>
        {/* Arm segment 1 */}
        <mesh position={[0, 0.15, 0]}>
          <boxGeometry args={[0.1, 0.5, 0.1]} />
          <meshStandardMaterial color="#9e9e9e" />
        </mesh>
        {/* Arm segment 2 - pivot top */}
        <mesh position={[0, 0.45, 0]}>
          <boxGeometry args={[0.1, 0.4, 0.1]} />
          <meshStandardMaterial color="#757575" />
        </mesh>
        {/* "Claw" or end effector */}
        <mesh position={[0, 0.65, 0]}>
          <boxGeometry args={[0.2, 0.05, 0.1]} />
          <meshStandardMaterial color="#bdbdbd" />
        </mesh>
      </group>
    );
  } else if (name === "Drone") {
    return (
      <group>
        {/* Drone body: a small box */}
        <mesh>
          <boxGeometry args={[0.4, 0.1, 0.4]} />
          <meshStandardMaterial color="#424242" />
        </mesh>
        {/* 4 propeller arms */}
        <mesh position={[0.3, 0, 0.3]}>
          <cylinderGeometry args={[0.01, 0.01, 0.5, 8]} />
          <meshStandardMaterial color="#9e9e9e" />
        </mesh>
        <mesh position={[0.3, 0, -0.3]}>
          <cylinderGeometry args={[0.01, 0.01, 0.5, 8]} />
          <meshStandardMaterial color="#9e9e9e" />
        </mesh>
        <mesh position={[-0.3, 0, 0.3]}>
          <cylinderGeometry args={[0.01, 0.01, 0.5, 8]} />
          <meshStandardMaterial color="#9e9e9e" />
        </mesh>
        <mesh position={[-0.3, 0, -0.3]}>
          <cylinderGeometry args={[0.01, 0.01, 0.5, 8]} />
          <meshStandardMaterial color="#9e9e9e" />
        </mesh>
        {/* 4 small "propellers" at ends */}
        <mesh position={[0.55, 0, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.1, 12]} />
          <meshStandardMaterial color="#bdbdbd" />
        </mesh>
        <mesh position={[0.55, 0, -0.3]} rotation={[Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.1, 12]} />
          <meshStandardMaterial color="#bdbdbd" />
        </mesh>
        <mesh position={[-0.55, 0, 0.3]} rotation={[Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.1, 12]} />
          <meshStandardMaterial color="#bdbdbd" />
        </mesh>
        <mesh position={[-0.55, 0, -0.3]} rotation={[Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.1, 12]} />
          <meshStandardMaterial color="#bdbdbd" />
        </mesh>
      </group>
    );
  } else if (name === "Smart House") {
    return (
      <group>
        {/* House base */}
        <mesh>
          <boxGeometry args={[1, 0.5, 1]} />
          <meshStandardMaterial color="#ffee58" />
        </mesh>
        {/* Roof as a triangular prism or just a second box. For simplicity, a bigger box on top. */}
        <mesh position={[0, 0.5, 0]}>
          <boxGeometry args={[1.2, 0.4, 1.2]} />
          <meshStandardMaterial color="#fbc02d" />
        </mesh>
        {/* Solar panel on top */}
        <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0.7, 0.3]}>
          <planeGeometry args={[0.6, 0.3]} />
          <meshStandardMaterial color="#263238" />
        </mesh>
      </group>
    );
  }
  return null;
}

/** Additional mini challenge for Grade 5 */
function Grade5AutomationChallenge() {
  const examples = [
    {
      name: "Warehouse Robot Picker",
      description: "Design a robot that retrieves items from high shelves automatically.",
      tip: "Use sensors to detect item location and avoid collisions!",
    },
    {
      name: "Greenhouse Automation",
      description: "An automated system to water plants and control temperature.",
      tip: "Soil moisture sensors can trigger water pumps as needed.",
    },
    {
      name: "Household Assistant Drone",
      description: "A small indoor drone to deliver snacks or follow you with a camera.",
      tip: "Lightweight design, stable flight control, and safety features!",
    },
  ];
  const [index, setIndex] = useState(0);
  const next = () => setIndex((prev) => (prev + 1) % examples.length);

  return (
    <div className="g5eng-challenge-container">
      <h4>Automation Challenge: {examples[index].name}</h4>
      <p className="g5eng-challenge-description">{examples[index].description}</p>
      <p className="g5eng-challenge-example">
        <strong>Tip:</strong> {examples[index].tip}
      </p>
      <button className="g5eng-next-challenge-btn" onClick={next}>
        See Another Idea
      </button>
    </div>
  );
}

/** The main Grade5EngineeringLesson component. */
export default function Grade5EngineeringLesson() {
  const [selectedTopic, setSelectedTopic] = useState(null);

  /** Lesson Content */
  const lessonContent = {
    roboticsAutomation: {
      title: "Robotics & Automation",
      description: "Learn how engineers design robots and automated systems to do tasks!",
      example: "Example: Factories use robot arms for faster, more precise assembly!",
      steps: [
        "Robotics involves mechanical designs (joints, motors) plus programming.",
        "Automation uses sensors & software to let machines operate with minimal human input.",
        "Engineers test safety features to ensure robots don’t harm humans or themselves.",
        "Modern manufacturing relies on robotics for efficiency!",
        <Grade5Engineering3DDemo key="g5eng-3d-demo" />,
      ],
    },
    greenBuilding: {
      title: "Green Building Concepts",
      description: "Learn how to make eco-friendly buildings!",
      example: "Example: Homes with solar panels, high insulation, or green roofs!",
      steps: [
        "Materials like recycled steel or bamboo reduce the environmental impact.",
        "Good insulation or shading helps reduce energy usage for heating/cooling.",
        "Engineers might add solar panels or wind turbines for on-site energy.",
        "Green roofs & rainwater collection also help sustain resources!",
        <Grade5AutomationChallenge key="g5eng-challenge" />,
      ],
    },
  };

  /** Quiz Pools => 25 each => total 50 */
  // 1) roboticsAutomationPool (25 Q)
  const roboticsAutomationPool = [
    {
      question: "What is one benefit of using robots in factories?",
      correctAnswer: "They can perform repetitive tasks quickly and precisely",
      options: [
        "They never need electricity",
        "They can perform repetitive tasks quickly and precisely",
        "They only work for short periods",
      ],
    },
    {
      question: "Sensors in a robot arm help with?",
      correctAnswer: "Detecting position or obstacles",
      options: [
        "Making the robot heavier",
        "Detecting position or obstacles",
        "Reducing programming time",
      ],
    },
    // ...
    {
      question: "Automation means?",
      correctAnswer: "Systems operating with minimal human input",
      options: [
        "Robots that are always controlled manually",
        "Systems operating with minimal human input",
        "Shutting down machines",
      ],
    },
  ];

  // 2) greenBuildingPool (25 Q)
  const greenBuildingPool = [
    {
      question: "A green building aims to?",
      correctAnswer: "Reduce environmental impact and save energy",
      options: [
        "Look only green in color",
        "Reduce environmental impact and save energy",
        "Have no windows at all",
      ],
    },
    {
      question: "Why install solar panels on a house?",
      correctAnswer: "To generate electricity from sunlight",
      options: [
        "To reflect more sunlight away",
        "To generate electricity from sunlight",
        "To block all heat from the roof",
      ],
    },
    // ...
    {
      question: "Insulation in walls and roofs helps by?",
      correctAnswer: "Keeping indoor temperature stable with less energy",
      options: [
        "Adding extra weight to the house",
        "Keeping indoor temperature stable with less energy",
        "Letting wind pass through freely",
      ],
    },
  ];

  /** Quick Quiz & Final Quiz states */
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes

  // Timer for final quiz
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  /** Generate Quick Quiz */
  const generateQuiz = (topic) => {
    let questionPool = topic === "roboticsAutomation" ? roboticsAutomationPool : greenBuildingPool;
    const item = questionPool[Math.floor(Math.random() * questionPool.length)];
    setQuiz({
      question: item.question,
      correctAnswer: item.correctAnswer,
      options: shuffleArray([...item.options]),
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  /** Handle Quick Quiz answer */
  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    // auto refresh question after short delay
    setTimeout(() => {
      if (selectedTopic) generateQuiz(selectedTopic);
    }, 1500);
  };

  /** Start final quiz => total 50 Q */
  const startFinalQuiz = () => {
    const combined = [...roboticsAutomationPool, ...greenBuildingPool];
    const finalSet = shuffleArray(combined);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  /** Final quiz handle */
  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex]?.correctAnswer === choice) {
      setFinalQuizScore(prev => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex(prev => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g5eng-page">
      <h1 className="g5eng-title">Grade 5 Engineering Lessons</h1>
      <p className="g5eng-description">
        Explore Robotics, Automation, and Green Building ideas!
      </p>

      <div className="g5eng-topics">
        <button
          className="g5eng-topic-card"
          onClick={() => {
            setSelectedTopic("roboticsAutomation");
            generateQuiz("roboticsAutomation");
          }}
        >
          Robotics &amp; Automation
        </button>
        <button
          className="g5eng-topic-card"
          onClick={() => {
            setSelectedTopic("greenBuilding");
            generateQuiz("greenBuilding");
          }}
        >
          Green Building
        </button>
      </div>

      {selectedTopic && (
        <div className="g5eng-lesson-quiz-container">
          {/* Lesson Content (Left) */}
          <div className="g5eng-lesson-content">
            <h2 className="g5eng-lesson-title">
              {lessonContent[selectedTopic].title}
            </h2>
            <p className="g5eng-lesson-description">
              {lessonContent[selectedTopic].description}
            </p>
            <p className="g5eng-lesson-example">
              <strong>{lessonContent[selectedTopic].example}</strong>
            </p>
            <ul className="g5eng-lesson-steps">
              {lessonContent[selectedTopic].steps.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ul>
          </div>

          {/* Right: Quizzes */}
          <div className="g5eng-quiz-double-container">
            {/* Quick Quiz */}
            <div className="g5eng-quick-quiz-container">
              <h2 className="g5eng-quiz-title">Quick Quiz!</h2>
              {quiz && (
                <>
                  <p className="g5eng-quiz-question">{quiz.question}</p>
                  <div className="g5eng-quiz-options">
                    {quiz.options.map((opt, i) => (
                      <button
                        key={i}
                        className={`g5eng-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                        onClick={() => handleAnswerClick(opt)}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                  {feedback && <p className="g5eng-quiz-feedback">{feedback}</p>}
                </>
              )}
            </div>

            {/* Final Quiz */}
            <div className="g5eng-final-quiz-container">
              <h2 className="g5eng-quiz-title">Final Quiz</h2>
              {!finalQuizActive && (
                <button className="g5eng-final-quiz-button" onClick={startFinalQuiz}>
                  Start Final Quiz (50 Questions)
                </button>
              )}
              {finalQuizActive && (
                <div className="g5eng-final-quiz-content">
                  <p>
                    Time Left: {Math.floor(timeLeft / 60)}:
                    {String(timeLeft % 60).padStart(2, "0")}
                  </p>
                  <p>
                    Question {finalQuizIndex + 1} of {finalQuizQuestions.length}
                  </p>
                  <p className="g5eng-quiz-question">
                    {finalQuizQuestions[finalQuizIndex].question}
                  </p>
                  <div className="g5eng-quiz-options">
                    {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                      <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                        {choice}
                      </button>
                    ))}
                  </div>
                  <p>Score: {finalQuizScore}</p>
                  {finalQuizIndex === finalQuizQuestions.length - 1 && (
                    <p>
                      Final Score: {finalQuizScore}/{finalQuizQuestions.length}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Link to="/engineering" className="g5eng-back-button">
        &larr; Back to Engineering Page
      </Link>
    </div>
  );
}
